import { useParams, Link, Navigate } from "react-router-dom";
import { useScrollDepth } from "@/lib/scrollTracking";
import { motion } from "framer-motion";
import { getArticleBySlug, allBlogArticles } from "@/data/blogArticles";
import { blogRedirects } from "@/data/blogRedirects";
import { isKeeper } from "@/data/blogKeepers";
import { getRelatedBlogArticles, getRelatedLearnTopicsForBlog } from "@/lib/relatedContent";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import PictureImg from "@/components/ui/PictureImg";
import SocialShare from "@/components/blog/SocialShare";
import ArticleContent from "@/components/blog/ArticleContent";
import SectionAtmosphere from "@/components/SectionAtmosphere";
import atmosphereCta from "@/assets/atmosphere-finalcta.jpg";
import { lqipFor } from "@/generated/atmospheric-lqip";
import { riseFade, easeStandard } from "@/lib/motion";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect } from "react";
import BookmarkButton from "@/components/blog/BookmarkButton";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import { useCanonical } from "@/lib/useCanonical";

const BlogArticle = () => {
  useScrollDepth("blog_article");
  const { slug } = useParams<{ slug: string }>();
  useCanonical({ path: slug ? `/blog/${slug}` : "/blog" });
  const redirectTo = slug && blogRedirects[slug];
  const article = !redirectTo && slug ? getArticleBySlug(slug) : undefined;


  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Set dynamic page title & meta
  useEffect(() => {
    if (!article) return;
    const SITE = "https://myelinahealth.com";
    const fullUrl = `${SITE}/blog/${article.slug}`;
    // Prefer a per-article OG card (generated by scripts/generate-og-images.mjs)
    // so social shares get a unique, title-branded thumbnail instead of the
    // shared hero image. Falls back to the hero image if the OG card is missing.
    const ogCard = `${SITE}/og/blog/${article.slug}.png`;
    const heroAbs = article.heroImage.startsWith("http")
      ? article.heroImage
      : `${SITE}${article.heroImage}`;
    const absImage = ogCard;
    void heroAbs;

    document.title = `${article.metaTitle} | Myelina Health`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", article.metaDescription);

    // Non-keeper articles are noindex'd so only the curated keepers get indexed.
    let robotsEl = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const prevRobots = robotsEl?.getAttribute("content") ?? null;
    if (!robotsEl) {
      robotsEl = document.createElement("meta");
      robotsEl.setAttribute("name", "robots");
      document.head.appendChild(robotsEl);
    }
    robotsEl.setAttribute("content", isKeeper(article.slug) ? "index, follow" : "noindex, follow");

    const setMeta = (prop: string, content: string) => {
      const isProp = prop.startsWith("og:") || prop.startsWith("article:");
      const attr = isProp ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${prop}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, prop);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("og:title", article.metaTitle);
    setMeta("og:description", article.metaDescription);
    setMeta("og:url", fullUrl);
    setMeta("og:type", "article");
    setMeta("og:image", absImage);
    setMeta("og:image:secure_url", absImage);
    setMeta("og:image:alt", article.heroAlt || article.title);
    setMeta("og:image:width", "1200");
    setMeta("og:image:height", "630");
    setMeta("og:site_name", "Myelina Health");
    setMeta("og:locale", "en_US");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", article.metaTitle);
    setMeta("twitter:description", article.metaDescription);
    setMeta("twitter:image", absImage);
    setMeta("twitter:image:alt", article.heroAlt || article.title);
    setMeta("article:published_time", new Date(article.publishDate).toISOString());
    setMeta("article:modified_time", new Date(article.publishDate).toISOString());
    setMeta("article:section", article.category);

    // article:tag, multiple values, so remove any prior tags then add fresh
    document
      .querySelectorAll('meta[property="article:tag"]')
      .forEach((el) => el.remove());
    (article.keywords || []).forEach((k) => {
      const el = document.createElement("meta");
      el.setAttribute("property", "article:tag");
      el.setAttribute("content", k);
      document.head.appendChild(el);
    });

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    const prevCanonical = canonical.href;
    canonical.href = fullUrl;

    const injectLd = (id: string, data: unknown) => {
      document.getElementById(id)?.remove();
      const s = document.createElement("script");
      s.id = id;
      s.type = "application/ld+json";
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    };

    injectLd("article-jsonld", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.metaDescription,
      image: [absImage],
      datePublished: new Date(article.publishDate).toISOString(),
      dateModified: new Date(article.publishDate).toISOString(),
      author: { "@type": "Organization", name: "Myelina Health", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Myelina Health",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
      articleSection: article.category,
      keywords: article.keywords.join(", "),
      inLanguage: "en",
      mainEntityOfPage: { "@type": "WebPage", "@id": fullUrl },
    });

    injectLd("article-breadcrumb-jsonld", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE}/blog` },
        { "@type": "ListItem", position: 3, name: article.title, item: fullUrl },
      ],
    });

    document.getElementById("article-faq-jsonld")?.remove();
    if (article.faqs && article.faqs.length > 0) {
      injectLd("article-faq-jsonld", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: article.faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      });
    }

    return () => {
      document.title = "Myelina Health | AI Energy Prediction for Women with MS";
      ["article-jsonld", "article-breadcrumb-jsonld", "article-faq-jsonld"].forEach(
        (id) => document.getElementById(id)?.remove(),
      );
      document
        .querySelectorAll('meta[property="article:tag"]')
        .forEach((el) => el.remove());
      if (canonical) canonical.href = prevCanonical || `${SITE}/`;
      if (robotsEl) {
        if (prevRobots === null) robotsEl.remove();
        else robotsEl.setAttribute("content", prevRobots);
      }
    };
  }, [article]);

  if (redirectTo) return <Navigate to={`/blog/${redirectTo}`} replace />;
  if (!article) return <Navigate to="/blog" replace />;

  const currentIndex = allBlogArticles.findIndex((a) => a.slug === slug);
  const nextArticle = allBlogArticles[(currentIndex + 1) % allBlogArticles.length];

  const relatedArticles = getRelatedBlogArticles(slug!, 3);
  const relatedLearn = getRelatedLearnTopicsForBlog(slug!, 2);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-32 pb-20">
        {/* ── Editorial header ─────────────────────────── */}
        <header className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Journal", href: "/blog" },
              { name: article.category, href: "/blog" },
              { name: article.title },
            ]}
          />
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-muted-foreground hover:text-primary transition-colors mb-10"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to library
          </Link>


          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easeStandard }}
            className="eyebrow mb-6"
          >
            {article.category}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.8, ease: easeStandard }}
            className="font-display text-foreground text-display-xl mb-8"
          >
            {article.title}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="flex flex-wrap items-center justify-between gap-4 text-[12px] text-muted-foreground/90 pb-2"
          >
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <span className="font-medium text-foreground/80">{article.author}</span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(article.publishDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {article.readTime}
              </span>
            </div>
            <SocialShare url={`/blog/${article.slug}`} title={article.title} description={article.metaDescription} />
            <div className="mt-4">
              <BookmarkButton
                slug={`blog/${article.slug}`}
                title={article.title}
                category={article.category}
                hero_image={article.heroImage}
                kind="article"
              />
            </div>
          </motion.div>

          <div className="hairline-fade mt-10" />
        </header>

        {/* ── Hero image ───────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: easeStandard }}
          className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-5xl my-12"
        >
          <div className="rounded-3xl overflow-hidden aspect-[16/9] shadow-[0_30px_80px_-30px_hsl(var(--foreground)/0.2)]">
            <PictureImg
              src={article.heroImage}
              alt={article.heroAlt}
              className="w-full h-full object-cover"
              width={1200}
              height={672}
              loading="eager"
              fetchPriority="high"
              sizes="(min-width: 1024px) 960px, 100vw"
            />


          </div>
        </motion.div>

        {/* ── Article body ─────────────────────────────── */}
        <article
          className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl prose prose-lg dark:prose-invert
            prose-headings:font-display prose-headings:text-foreground prose-headings:tracking-tight
            prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-5
            prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-4 prose-h3:italic prose-h3:text-primary
            prose-p:text-foreground/80 prose-p:leading-[1.75] prose-p:font-light
            prose-a:text-primary prose-a:font-medium prose-a:no-underline hover:prose-a:underline
            prose-strong:text-foreground prose-strong:font-medium
            prose-blockquote:border-l-2 prose-blockquote:border-primary prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:font-display prose-blockquote:text-2xl prose-blockquote:text-foreground/90 prose-blockquote:not-italic
            prose-li:text-foreground/80
            prose-table:text-sm
            prose-th:text-foreground prose-th:font-semibold
            prose-td:text-muted-foreground
          "
        >
          <ArticleContent content={article.content} />
        </article>

        {/* ── FAQ ─────────────────────────────────────── */}
        {article.faqs && article.faqs.length > 0 && (
          <section
            aria-labelledby="article-faq-heading"
            className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl mt-20"
          >
            <div className="eyebrow mb-4">Frequently asked</div>
            <h2
              id="article-faq-heading"
              className="font-display text-3xl sm:text-display-md text-foreground mb-8 tracking-tight"
            >
              Questions women with MS keep asking
            </h2>
            <dl className="divide-y divide-foreground/10 border-y border-foreground/10">
              {article.faqs.map((f, i) => (
                <div key={i} className="py-6">
                  <dt className="font-display text-xl text-foreground italic mb-3">
                    {f.question}
                  </dt>
                  <dd className="text-foreground/80 leading-[1.75] font-light">
                    {f.answer}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* ── CTA ─────────────────────────────────────── */}
        <div className="mt-20">
          <SectionAtmosphere
            ariaLabelledBy="article-cta-heading"
            src={atmosphereCta}
            optimizedBase="/opt/atmosphere-finalcta"
            lqip={lqipFor(atmosphereCta)}
            origin="center"
            intensity={0.13}
            glow="center"
            tone="cream"
            className="py-24"
          >
            <div className="container mx-auto px-6 sm:px-10 lg:px-16">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-10% 0px" }}
                variants={riseFade}
                className="max-w-2xl mx-auto text-center space-y-6"
              >
                <div className="eyebrow justify-center mx-auto">From reading to doing</div>
                <h3 id="article-cta-heading" className="font-display text-display-md text-foreground">
                  Ready to <span className="italic text-primary">plan around your energy?</span>
                </h3>
                <p className="lead-italic">
                  Myelina turns a quiet morning check-in into a calm Energy Map, so the day stops
                  surprising you.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                  <Link to="/signup">
                    <Button variant="premium" size="lg" className="group w-full sm:w-auto px-8">
                      Request access
                      <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>
          </SectionAtmosphere>
        </div>

        {/* ── Related ─────────────────────────────────── */}
        {relatedArticles.length > 0 && (
          <section className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-5xl mt-24" aria-labelledby="article-related-heading">
            <h2 id="article-related-heading" className="eyebrow mb-6">Related reading</h2>
            <div className="grid gap-6 sm:grid-cols-3">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/blog/${ra.slug}`}
                  className="group rounded-2xl border border-foreground/10 hover:border-foreground/20 overflow-hidden transition-all duration-500 hover:-translate-y-1 bg-background/60 backdrop-blur-sm"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <PictureImg
                      src={ra.heroImage}
                      alt={ra.heroAlt}
                      className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                      loading="lazy"
                      width={400}
                      height={225}
                      sizes="(min-width: 640px) 33vw, 100vw"
                    />


                  </div>
                  <div className="p-5">
                    <span className="eyebrow mb-2">{ra.category}</span>
                    <h3 className="font-display italic text-lg text-foreground group-hover:text-primary transition-colors mt-2 line-clamp-2 leading-tight">
                      {ra.title}
                    </h3>
                    <span className="text-[11px] text-muted-foreground mt-2 block">{ra.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ── Learn cross-links ────────────────────────── */}
        {relatedLearn.length > 0 && (
          <section
            className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-5xl mt-16"
            aria-labelledby="article-learn-heading"
          >
            <h2 id="article-learn-heading" className="eyebrow mb-6">Go deeper in the Learn library</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {relatedLearn.map((lt) => (
                <Link
                  key={lt.slug}
                  to={`/learn/${lt.slug}`}
                  className="group block rounded-2xl border border-foreground/10 hover:border-foreground/20 p-5 transition-all hover:-translate-y-0.5 bg-background/60 backdrop-blur-sm"
                >
                  <span className="eyebrow block mb-2">Learn</span>
                  <h3 className="font-display italic text-lg text-foreground group-hover:text-primary transition-colors leading-tight">
                    {lt.title}
                  </h3>
                  <p className="text-[13px] text-muted-foreground mt-2 line-clamp-2">{lt.description}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ── Next article ────────────────────────────── */}
        <div className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl mt-16">
          <Link
            to={`/blog/${nextArticle.slug}`}
            className="group flex items-center justify-between p-7 rounded-2xl border border-foreground/10 hover:border-foreground/20 transition-all hover:-translate-y-0.5 bg-background/60 backdrop-blur-sm"
          >
            <div>
              <span className="eyebrow mb-2 block">Next article</span>
              <h3 className="font-display italic text-xl text-foreground group-hover:text-primary transition-colors">
                {nextArticle.title}
              </h3>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogArticle;
